import { NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useHealthAlertCount } from "../../../hooks/useHealthAlertCount";
import { DASHBOARD_NAV_ITEMS } from "../dashboardNavConfig";
import { useAuth } from "../../../context/AuthContext";

export default function Sidebar({ isCollapsed, setIsCollapsed }) {
  const alertCount = useHealthAlertCount();
  const { user } = useAuth();
  
  const displayName = user?.name || user?.email?.split("@")[0] || "User";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  return (
    <aside className={`hidden md:flex flex-col fixed inset-y-0 left-0 bg-white border-r border-gray-200 transition-all duration-300 z-40 ${isCollapsed ? "w-[80px]" : "w-[260px]"}`}>
      {/* Floating Toggle Button */}
      <button
        type="button"
        onClick={toggleCollapse}
        className="absolute -right-3.5 top-8 z-50 flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition-all duration-200 hover:scale-110 hover:text-blue-600 hover:shadow"
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {isCollapsed ? <ChevronRightIcon sx={{ fontSize: 18 }} /> : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
      </button>

      <div className="flex h-full flex-col min-h-0 overflow-y-hidden">
        {/* Logo Section */}
        <div className={`flex h-16 shrink-0 items-center mt-2 mb-2 ${isCollapsed ? "justify-center px-0" : "px-6"}`}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-violet-600 text-white shadow-sm shadow-blue-500/20">
              <AutoAwesomeIcon sx={{ fontSize: 20 }} />
            </div>
            <AnimatePresence mode="wait">
              {!isCollapsed && (
                <motion.span
                  className="truncate text-[1.05rem] font-semibold text-gray-900 tracking-tight"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  HealthTimeline
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
          {DASHBOARD_NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const badge = item.badgeFromCount && alertCount > 0 ? alertCount : item.badge != null ? item.badge : null;
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                title={isCollapsed ? item.label : ""}
                className={({ isActive }) =>
                  `group relative flex items-center ${isCollapsed ? "justify-center" : "justify-between"} rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? "bg-blue-50 text-blue-600 ring-1 ring-blue-500/10 shadow-[0_1px_2px_rgba(59,130,246,0.05)]"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <div className={`flex items-center gap-3 min-w-0 ${isCollapsed ? "justify-center" : ""}`}>
                      <Icon className="shrink-0" sx={{ fontSize: 22 }} />
                      <AnimatePresence mode="wait">
                        {!isCollapsed && (
                          <motion.span
                            className="truncate"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.2 }}
                          >
                            {item.label}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </div>
                    {badge != null && badge !== 0 && (
                      !isCollapsed ? (
                        <span className="flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white shadow-sm ring-2 ring-white">
                          {badge > 9 ? "9+" : badge}
                        </span>
                      ) : (
                        <span className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full border-2 border-white bg-red-500" />
                      )
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer Profile */}
        <div className="mt-auto shrink-0 border-t border-gray-200 bg-gray-50/50 p-4 w-full">
          <div className={`flex items-center gap-3 overflow-hidden ${isCollapsed ? "justify-center" : ""}`}>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-700 shadow-sm ring-2 ring-white transition-all hover:shadow">
              {initials}
            </div>
            <AnimatePresence mode="wait">
              {!isCollapsed && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="min-w-0 flex-1 whitespace-nowrap"
                >
                  <p className="truncate text-sm font-semibold text-gray-900">{displayName}</p>
                  <p className="truncate text-xs font-medium text-gray-500">Pro Member</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </aside>
  );
}
